import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorNotice, Layout, LoadingScreen } from '../components/Layout';
import { api, ApiClientError } from '../lib/api';
import type { MeetingLabel, MeetingNode, MeetingTopicSummary } from '../lib/types';

function formatDate(value: string | null) {
  if (!value) return 'ยังไม่มีการประชุม';
  return new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium' }).format(new Date(value));
}

export function MeetingHubPage() {
  const client = useQueryClient();
  const [query, setQuery] = useState('');
  const [includeTranscript, setIncludeTranscript] = useState(false);
  const [labelId, setLabelId] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingTopic, setEditingTopic] = useState<MeetingTopicSummary | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [viewFilter, setViewFilter] = useState<'unclassified' | 'tracking'>('unclassified');

  const labels = useQuery({
    queryKey: ['meeting-labels'],
    queryFn: () =>
      api<{ data: MeetingLabel[] }>('/api/meeting-labels').then((result) => result.data),
  });
  const topics = useQuery({
    queryKey: ['meeting-topics', labelId],
    queryFn: () =>
      api<{ data: MeetingTopicSummary[] }>(
        `/api/meeting-topics?status=active&limit=100${labelId ? `&labelId=${encodeURIComponent(labelId)}` : ''}`,
      ).then((result) => result.data),
  });
  const search = useQuery({
    queryKey: ['meeting-search', query, includeTranscript, labelId],
    queryFn: () =>
      api<{ data: Array<MeetingNode & { topicTitle: string }> }>(
        `/api/meeting-search?query=${encodeURIComponent(query)}&includeTranscript=${includeTranscript}${labelId ? `&labelId=${encodeURIComponent(labelId)}` : ''}`,
      ).then((result) => result.data),
    enabled: query.trim().length >= 2,
  });
  const unclassified = useQuery({ queryKey: ['meeting-unclassified'], queryFn: () => api<{ data: Array<{ id: string; title: string; appointment_date: string | null; start_at: string | null }> }>('/api/meeting-unclassified').then((result) => result.data), enabled: viewFilter === 'unclassified' });
  const createTopic = useMutation({
    mutationFn: () =>
      api<{ data: MeetingTopicSummary }>('/api/meeting-topics', {
        method: 'POST',
        body: JSON.stringify({ title, description: description || null, color: '#0f766e' }),
      }),
    onSuccess: async () => {
      setTitle('');
      setDescription('');
      setShowCreate(false);
      await client.invalidateQueries({ queryKey: ['meeting-topics'] });
    },
  });
  const updateTopic = useMutation({
    mutationFn: () => api(`/api/meeting-topics/${editingTopic?.id}`, { method: 'PATCH', body: JSON.stringify({ title, description: description || null }) }),
    onSuccess: async () => { setEditingTopic(null); setTitle(''); setDescription(''); await client.invalidateQueries({ queryKey: ['meeting-topics'] }); },
  });
  const onCreate = (event: FormEvent) => {
    event.preventDefault();
    if (title.trim()) createTopic.mutate();
  };
  if (topics.isLoading || labels.isLoading) {
    return (
      <Layout title="เรื่องประชุม" wide>
        <LoadingScreen message="กำลังเปิดศูนย์บัญชาการ…" />
      </Layout>
    );
  }
  const topicRows = topics.data ?? [];
  return (
    <Layout title="ศูนย์บัญชาการเรื่องประชุม" wide>
      <div className="space-y-5">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-teal-950 to-teal-800 p-5 text-white shadow-xl shadow-teal-950/15 md:p-7">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-teal-200">MEETING KNOWLEDGE CENTER</p>
              <h2 className="mt-2 text-2xl font-bold md:text-3xl">
                การประชุม
              </h2>
              <p className="mt-2 text-sm text-teal-50/80">
                ค้นจากสรุปเป็นค่าเริ่มต้น และเปิดค้น Transcript เฉพาะเมื่อต้องการ
              </p>
            </div>
            <button
              type="button"
              className="min-h-12 rounded-xl bg-white px-5 font-bold text-teal-900"
              onClick={() => setShowCreate(true)}
            >
              ＋ สร้างเรื่องประชุม
            </button>
          </div>
        </section>

        <section className="card"><div className="flex flex-wrap gap-2"><button className={viewFilter === 'unclassified' ? 'btn-primary' : 'btn-secondary'} onClick={() => setViewFilter('unclassified')}>รอจัดหมวดหมู่</button><button className={viewFilter === 'tracking' ? 'btn-primary' : 'btn-secondary'} onClick={() => setViewFilter('tracking')}>เรื่องกำลังติดตาม ({topicRows.length})</button></div>{viewFilter === 'unclassified' && <div className="mt-4 space-y-2">{unclassified.data?.map((item) => <Link key={item.id} to={`/appointments/${item.id}/review`} className="block rounded-xl border p-3"><p className="font-semibold">{item.title || 'นัดประชุม'}</p><p className="text-sm text-slate-500">{item.appointment_date ?? 'ยังไม่ระบุวันที่'} · กดเพื่อจัดหมวดหมู่</p></Link>)}{unclassified.data && !unclassified.data.length && <p className="text-sm text-slate-500">ไม่มีรายการรอจัดหมวดหมู่</p>}</div>}</section>

        <section className="card">
          <div className="grid gap-3 lg:grid-cols-[1fr_15rem]">
            <label>
              <span className="label">ค้นข้อมูลย้อนหลัง</span>
              <input
                className="field"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="ค้นหัวข้อ มติ สรุป หรือรายละเอียด…"
              />
            </label>
            <label>
              <span className="label">กรองป้ายสำคัญ</span>
              <select
                className="field"
                value={labelId}
                onChange={(event) => setLabelId(event.target.value)}
              >
                <option value="">ทุกป้าย</option>
                {labels.data?.map((label) => (
                  <option key={label.id} value={label.id}>
                    {label.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="mt-3 flex min-h-11 cursor-pointer items-center gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-5 w-5 accent-teal-700"
              checked={includeTranscript}
              onChange={(event) => setIncludeTranscript(event.target.checked)}
            />
            <span>
              <strong>ค้นใน Transcript ด้วย</strong> — อาจใช้เวลามากขึ้นและพบข้อความละเอียดกว่าสรุป
            </span>
          </label>
        </section>

        {query.trim().length >= 2 && (
          <section className="card">
            <h2 className="font-bold">ผลการค้นหา</h2>
            {search.isFetching ? (
              <p className="mt-3 text-sm text-slate-500">กำลังค้นหา…</p>
            ) : search.data?.length ? (
              <div className="mt-3 divide-y divide-slate-100">
                {search.data.map((meeting) => (
                  <Link
                    key={meeting.id}
                    to={`/meeting-topics/${meeting.topicId}?meeting=${meeting.id}`}
                    className="block py-3"
                  >
                    <p className="font-semibold text-slate-900">{meeting.title}</p>
                    <p className="text-sm text-teal-700">{meeting.topicTitle}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-600">
                      {meeting.summary ||
                        meeting.decisions ||
                        'มีข้อความตรงกับคำค้นในข้อมูลที่เลือก'}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500">ไม่พบข้อมูลที่ตรงกัน</p>
            )}
          </section>
        )}

        <section className={viewFilter === 'tracking' ? '' : 'hidden'}>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">เรื่องที่กำลังติดตาม</h2>
              <p className="text-sm text-slate-500">
                เลือกเรื่องเพื่อดูต้นไม้และการประชุมที่แตกแขนง
              </p>
            </div>
          </div>
          {topics.isError && <ErrorNotice message="โหลดรายการเรื่องประชุมไม่สำเร็จ" />}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {topicRows.map((topic) => (
              <div
                key={topic.id}
                className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md"
              >
                <Link to={`/meeting-topics/${topic.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: topic.color }}
                  />
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                    {topic.meetingCount} ครั้ง
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-slate-900 group-hover:text-teal-800">
                  {topic.title}
                </h3>
                <p className="mt-1 line-clamp-2 min-h-10 text-sm text-slate-500">
                  {topic.description || 'ยังไม่มีคำอธิบายเรื่อง'}
                </p>
                <p className="mt-4 text-xs font-medium text-slate-500">
                  ล่าสุด: {formatDate(topic.latestMeetingAt)}
                </p>
                </Link>
                {!topic.systemKey && <button type="button" className="mt-3 text-sm font-semibold text-teal-700" onClick={() => { setEditingTopic(topic); setTitle(topic.title); setDescription(topic.description ?? ''); }}>แก้ไขเรื่องประชุม</button>}
              </div>
            ))}
          </div>
          {!topicRows.length && (
            <div className="card py-10 text-center">
              <p className="font-semibold">ยังไม่มีเรื่องประชุม</p>
              <p className="mt-1 text-sm text-slate-500">
                สร้างเรื่องแรก แล้วเพิ่มการประชุมครั้งต่อไปเป็นแขนงได้
              </p>
            </div>
          )}
        </section>
      </div>

      {(showCreate || editingTopic) && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/50 p-0 sm:items-center sm:justify-center sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <form
            onSubmit={onCreate}
            className="w-full rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-3xl"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">{editingTopic ? 'แก้ไขเรื่องประชุม' : 'สร้างเรื่องประชุม'}</h2>
              <button
                type="button"
                className="px-3 text-slate-500"
                onClick={() => { setShowCreate(false); setEditingTopic(null); }}
              >
                ✕
              </button>
            </div>
            <label className="mt-5 block">
              <span className="label">ชื่อเรื่อง *</span>
              <input
                autoFocus
                className="field"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={200}
              />
            </label>
            <label className="mt-3 block">
              <span className="label">คำอธิบาย</span>
              <textarea
                className="field min-h-24"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={4000}
              />
            </label>
            {(createTopic.error || updateTopic.error) && (
              <div className="mt-3">
                <ErrorNotice
                  message={
                    createTopic.error instanceof ApiClientError
                      ? (createTopic.error ?? updateTopic.error)?.message
                      : editingTopic ? 'แก้ไขเรื่องไม่สำเร็จ' : 'สร้างเรื่องไม่สำเร็จ'
                  }
                />
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button type="button" className="btn-secondary" onClick={() => { setShowCreate(false); setEditingTopic(null); }}>
                ยกเลิก
              </button>
              <button className="btn-primary" disabled={!title.trim() || createTopic.isPending || updateTopic.isPending} onClick={(event) => { if (editingTopic) { event.preventDefault(); updateTopic.mutate(); } }}>
                {createTopic.isPending || updateTopic.isPending ? 'กำลังบันทึก…' : editingTopic ? 'บันทึกการแก้ไข' : 'สร้างเรื่อง'}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
